import type { FontDto, FontStyleDto } from "@repo/api/font";
import { createContext, useContext, useEffect, useState } from "react";
import {
  cancelRender,
  continueRender,
  delayRender,
  getRemotionEnvironment,
} from "remotion";

export const FontContext = createContext<Record<string, FontDto>>({});

const fontLoads = new Map<string, Promise<string>>();
const loadedFontFamilies = new Map<string, string>();
const fontLoadListeners = new Set<() => void>();

const getFontKey = (
  fontExternalId: string,
  weight: number,
  style: FontStyleDto,
) => `${fontExternalId}:${weight}:${style}`;

function pickFontVariant(font: FontDto, weight: number, style: FontStyleDto) {
  const variants = font.metadata.variants;

  if (variants.length === 0) {
    return undefined;
  }

  const sameStyle = variants.filter((variant) => variant.style === style);
  const pool = sameStyle.length > 0 ? sameStyle : variants;

  return pool.reduce((closest, variant) =>
    Math.abs(variant.weight - weight) < Math.abs(closest.weight - weight)
      ? variant
      : closest,
  );
}

export function getLoadedFontFamily(
  fontExternalId: string,
  weight: number,
  style: FontStyleDto,
): string | null {
  return (
    loadedFontFamilies.get(getFontKey(fontExternalId, weight, style)) ?? null
  );
}

export function subscribeToFontLoads(listener: () => void) {
  fontLoadListeners.add(listener);

  return () => {
    fontLoadListeners.delete(listener);
  };
}

const RETRY_DELAYS_MS = [500, 1000];

async function loadFontFace(
  fontFamily: string,
  url: string,
  weight: number,
  style: FontStyleDto,
) {
  for (let attempt = 0; ; attempt++) {
    try {
      const fontFace = new FontFace(fontFamily, `url("${url}")`, {
        weight: String(weight),
        style,
      });

      return await fontFace.load();
    } catch (error) {
      const delay = RETRY_DELAYS_MS[attempt];

      if (delay === undefined) {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

export function loadCustomFont(
  font: FontDto,
  weight: number,
  style: FontStyleDto,
): Promise<string> {
  const key = getFontKey(font.externalId, weight, style);
  const existing = fontLoads.get(key);

  if (existing) {
    return existing;
  }

  const load = (async () => {
    const variant = pickFontVariant(font, weight, style);

    if (!variant) {
      throw new Error(`No font variant available for ${font.family}`);
    }

    const fontFamily = `custom-font-${font.externalId}`;

    document.fonts.add(
      await loadFontFace(fontFamily, variant.url, weight, style),
    );
    loadedFontFamilies.set(key, fontFamily);
    fontLoadListeners.forEach((listener) => listener());

    return fontFamily;
  })();

  fontLoads.set(key, load);

  return load;
}

export function useFont(options: {
  fontExternalId: string;
  fontWeight?: number;
  fontStyle?: FontStyleDto;
}): string | null {
  const fonts = useContext(FontContext);
  const fontExternalId = options.fontExternalId;
  const fontWeight = options.fontWeight ?? 400;
  const fontStyle = options.fontStyle ?? "normal";
  const customFont = fonts[fontExternalId];
  const fontKey = getFontKey(fontExternalId, fontWeight, fontStyle);

  const [loaded, setLoaded] = useState<{
    fontKey: string;
    fontFamily: string | null;
  } | null>(null);
  const [delayHandle] = useState(() => delayRender());

  useEffect(() => {
    let isCurrent = true;

    async function delayLoadFont() {
      let fontFamily: string | null = null;

      try {
        if (!customFont) {
          throw new Error(`Font ${fontExternalId} not found`);
        }

        fontFamily = await loadCustomFont(customFont, fontWeight, fontStyle);
      } catch (error) {
        if (getRemotionEnvironment().isRendering) {
          cancelRender(error);
        }
      }

      if (isCurrent) {
        setLoaded({ fontKey, fontFamily });
      }

      continueRender(delayHandle);
    }

    delayLoadFont();

    return () => {
      isCurrent = false;
    };
  }, [customFont, fontExternalId, fontWeight, fontStyle, fontKey, delayHandle]);

  // Never return a family loaded for a different face, the browser would synthesize it.
  return loaded?.fontKey === fontKey ? loaded.fontFamily : null;
}
